import type { KeepWorkingPausedReason, SessionTasks } from '@localagent/sdk';
import { cn } from '@localagent/ui';

type KeepWorkingDisplay = 'off' | 'on' | 'waiting_on_user' | 'paused' | 'complete';

const PAUSE_REASON: Record<KeepWorkingPausedReason, string> = {
  user_stopped: 'stopped by you',
  run_failed: 'a run failed',
  not_entitled: 'not entitled',
  check_failed: 'the check failed',
};

const DISPLAY_CLASS: Record<KeepWorkingDisplay, string> = {
  off: 'bg-muted text-muted-foreground',
  on: 'bg-info-subtle text-info-text',
  waiting_on_user: 'bg-warning-subtle text-warning-text',
  paused: 'bg-destructive/10 text-destructive',
  complete: 'bg-success-subtle text-success-text',
};

function keepWorkingDisplay(tasks: SessionTasks | null): { state: KeepWorkingDisplay; label: string } {
  if (!tasks || !tasks.keep_working.on) return { state: 'off', label: 'Off' };
  if (tasks.keep_working.paused_reason) {
    return { state: 'paused', label: `Paused · ${PAUSE_REASON[tasks.keep_working.paused_reason]}` };
  }
  switch (tasks.check_state) {
    case 'waiting_on_user':
      return { state: 'waiting_on_user', label: 'Waiting on you' };
    case 'complete':
      return { state: 'complete', label: 'Complete' };
    case 'idle':
      return { state: 'on', label: 'On' };
  }
}

export interface KeepWorkingControlProps {
  on: boolean;
  onChange?: (on: boolean) => void;
  testIdPrefix?: string;
}

export function KeepWorkingControl({ on, onChange, testIdPrefix = '' }: KeepWorkingControlProps) {
  const id = `${testIdPrefix}keep-working-toggle`;
  return (
    <label
      htmlFor={id}
      className={cn(
        'flex flex-none items-center gap-1.5 whitespace-nowrap text-micro',
        onChange ? 'text-foreground' : 'text-muted-foreground',
      )}
    >
      <input
        id={id}
        data-testid={id}
        type="checkbox"
        role="switch"
        aria-checked={on}
        checked={on}
        disabled={!onChange}
        onChange={(e) => onChange?.(e.target.checked)}
      />
      Keep working
    </label>
  );
}

export interface KeepWorkingStateLineProps {
  tasks: SessionTasks | null;
  testIdPrefix?: string;
}

export function KeepWorkingStateLine({ tasks, testIdPrefix = '' }: KeepWorkingStateLineProps) {
  const { state, label } = keepWorkingDisplay(tasks);
  return (
    <span
      data-testid={`${testIdPrefix}keep-working-state`}
      data-state={state}
      className={cn('inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-micro', DISPLAY_CLASS[state])}
    >
      Keep working: {label}
    </span>
  );
}
